import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { MapPin, Flag, Car } from "lucide-react";

const ICONS = { pickup: MapPin, dropoff: Flag, driver: Car };
const DEFAULT_CENTER = [29.8833, -97.9414];

const makeIcon = (kind) => {
  const Icon = ICONS[kind];
  return L.divIcon({
    className: "",
    html: `<div class="scoopz-pin scoopz-pin-${kind}">${renderToStaticMarkup(<Icon size={16} strokeWidth={2.5} />)}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
  });
};

function FitView({ points }) {
  const map = useMap();
  const key = JSON.stringify(points);
  useEffect(() => {
    if (!points.length) {
      map.setView(DEFAULT_CENTER, 11);
      return;
    }
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    const wide = window.innerWidth >= 768;
    map.fitBounds(L.latLngBounds(points), {
      paddingTopLeft: wide ? [500, 120] : [40, 110],
      paddingBottomRight: wide ? [70, 60] : [40, window.innerHeight * 0.68],
    });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function MapView({ pickup, dropoff, driver, route, onMove, className = "fixed inset-0 z-0" }) {
  const icons = useMemo(() => ({ pickup: makeIcon("pickup"), dropoff: makeIcon("dropoff"), driver: makeIcon("driver") }), []);
  const points = [pickup, dropoff].filter(Boolean).map((p) => [p.lat, p.lng]);
  if (!points.length && driver) points.push([driver.lat, driver.lng]);

  return (
    <div className={className} data-testid="map-view">
      <MapContainer center={DEFAULT_CENTER} zoom={11} zoomControl={false} className="h-full w-full">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        {route?.length > 1 && (
          <>
            <Polyline positions={route} pathOptions={{ color: "hsl(84, 100%, 55%)", weight: 14, opacity: 0.25 }} />
            <Polyline positions={route} pathOptions={{ color: "hsl(84, 100%, 55%)", weight: 5 }} />
          </>
        )}
        {pickup && <Marker position={[pickup.lat, pickup.lng]} icon={icons.pickup} draggable={!!onMove}
          eventHandlers={{ dragend: (e) => onMove?.("pickup", e.target.getLatLng()) }} />}
        {dropoff && <Marker position={[dropoff.lat, dropoff.lng]} icon={icons.dropoff} draggable={!!onMove}
          eventHandlers={{ dragend: (e) => onMove?.("dropoff", e.target.getLatLng()) }} />}
        {driver && <Marker position={[driver.lat, driver.lng]} icon={icons.driver} />}
        <FitView points={points} />
      </MapContainer>
    </div>
  );
}
